import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { CancelInterviewButton } from "./CancelInterviewButton";
import { CopyZoomJoinUrlButton } from "./CopyZoomJoinUrlButton";
import { resolveInterviewDetailPath } from "./detailPath";
import { SUMMARY_CHECKABLE_STATUSES } from "./status";

interface InterviewRowActionsProps {
  interview: {
    interviewId: string;
    status: string;
    zoomMeetingUrl: string | null;
    zoomRecordingViewUrl: string | null;
    stakeholderContext: { recipientNames: string[] } | null;
    candidateName?: string | null;
    startTime: string;
    endTime: string;
    sponsorName: string | null;
  };
  onInterviewCancelled?: () => void;
}

export function InterviewRowActions({
  interview,
  onInterviewCancelled,
}: InterviewRowActionsProps) {
  const navigate = useNavigate();
  const detailPath = resolveInterviewDetailPath(interview);
  // ステークホルダーは AI 要約を作らないため、チェックする対象がない
  const canCheckSummary =
    !interview.stakeholderContext &&
    SUMMARY_CHECKABLE_STATUSES.includes(interview.status);

  return (
    <div className="flex gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate(detailPath)}
        className="gap-1 text-[var(--color-text-secondary)] text-xs"
      >
        <svg
          className="h-3 w-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
        詳細
      </Button>
      {canCheckSummary && (
        <Button
          size="sm"
          onClick={() =>
            navigate(
              `/employee_interview/summary-check/${interview.interviewId}`,
            )
          }
          className="gap-1 text-xs"
        >
          <svg
            className="h-3 w-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          要約チェック
        </Button>
      )}
      {interview.status === "SCHEDULED" && (
        <CancelInterviewButton
          interviewId={interview.interviewId}
          candidateName={interview.candidateName ?? null}
          schedule={{
            startTime: interview.startTime,
            endTime: interview.endTime,
          }}
          sponsorName={interview.sponsorName}
          onCancelled={onInterviewCancelled}
        />
      )}
      {interview.zoomMeetingUrl && (
        <>
          <CopyZoomJoinUrlButton zoomMeetingUrl={interview.zoomMeetingUrl} />
          <a
            href={interview.zoomMeetingUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Zoom"
            title="Zoomミーティングを開く"
            className="flex cursor-pointer items-center gap-1 rounded-md border-none bg-transparent px-2 py-1 text-[var(--color-text-secondary)] text-xs no-underline hover:bg-[var(--color-bg-subtle-gray)]"
          >
            <svg
              className="h-3 w-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </a>
        </>
      )}
      {interview.zoomRecordingViewUrl && (
        <a
          href={interview.zoomRecordingViewUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="録画を見る"
          title="録画を見る"
          className="flex cursor-pointer items-center gap-1 rounded-md border-none bg-transparent px-2 py-1 text-[var(--color-text-secondary)] text-xs no-underline hover:bg-[var(--color-bg-subtle-gray)]"
        >
          <svg
            className="h-3 w-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14.752 11.168l-3.197-1.85A1 1 0 0010 10.185v3.63a1 1 0 001.555.832l3.197-1.85a1 1 0 000-1.664z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </a>
      )}
    </div>
  );
}
